'use client';

/**
 * Zakup „Trybu TV" na telefonie — Google Play Billing w aplikacji TWA (Digital Goods API +
 * Payment Request). Działa tylko w aplikacji z Google Play (com.kroniki_krolestwa.twa)
 * zbudowanej z funkcją playBilling (twa/twa-manifest.json). W zwykłej przeglądarce
 * isAvailable() → false i UI kieruje do aplikacji.
 *
 * Token zakupu weryfikuje serwer (POST /api/tv/license/account/verify) i przypisuje licencję
 * do konta zalogowanego gracza; serwer też potwierdza zakup w Google Play (acknowledge).
 */
const PLAY_BILLING = 'https://play.google.com/billing';
export const TV_PRODUCT_ID = 'tryb_tv';
const API = () => `${process.env.NEXT_PUBLIC_SOCKET_URL}/api/tv/license`;
const authHeaders = () => {
  let token = null;
  try { token = localStorage.getItem('token'); } catch { /* brak storage */ }
  return token ? { Authorization: `Bearer ${token}` } : {};
};

async function getService() {
  if (typeof window === 'undefined' || !('getDigitalGoodsService' in window)) return null;
  try {
    return await window.getDigitalGoodsService(PLAY_BILLING);
  } catch {
    return null; // przeglądarka / aplikacja bez Play Billing
  }
}

/** Cena z Google Play (np. „9,99 zł”) albo null, gdy zakup na tym urządzeniu niedostępny. */
export async function getTvProductPrice() {
  const service = await getService();
  if (!service) return null;
  try {
    const [item] = await service.getDetails([TV_PRODUCT_ID]);
    if (!item) return null;
    const { currency, value } = item.price || {};
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(Number(value));
  } catch {
    return null;
  }
}

async function verifyOnServer(purchaseToken) {
  const res = await fetch(`${API()}/account/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ purchaseToken, productId: TV_PRODUCT_ID }),
  });
  const data = await res.json().catch(() => ({}));
  if (!data?.ok) throw Object.assign(new Error(data?.error || 'VERIFY_FAILED'), { code: data?.error || 'VERIFY_FAILED' });
  return data.license;
}

/** Zakup przez Google Play → licencja na koncie. Rzuca { code: 'CANCELED' | 'UNAVAILABLE' | kod serwera }. */
export async function purchaseTvOnPhone() {
  const service = await getService();
  if (!service) throw Object.assign(new Error('UNAVAILABLE'), { code: 'UNAVAILABLE' });
  const request = new PaymentRequest(
    [{ supportedMethods: PLAY_BILLING, data: { sku: TV_PRODUCT_ID } }],
    { total: { label: 'Total', amount: { currency: 'PLN', value: '0' } } },
  );
  let response;
  try {
    response = await request.show();
  } catch (e) {
    throw Object.assign(new Error('CANCELED'), { code: e?.name === 'AbortError' ? 'CANCELED' : 'UNAVAILABLE' });
  }
  const purchaseToken = response?.details?.purchaseToken;
  try {
    if (!purchaseToken) throw Object.assign(new Error('NO_TOKEN'), { code: 'VERIFY_FAILED' });
    const license = await verifyOnServer(purchaseToken);
    await response.complete('success');
    return license;
  } catch (e) {
    try { await response.complete('fail'); } catch { /* już zamknięte */ }
    throw e;
  }
}

/** Przywrócenie: zakup z tego konta Google (np. nowy telefon) → ponowna weryfikacja na koncie gracza. */
export async function restoreTvOnPhone() {
  const service = await getService();
  if (!service) throw Object.assign(new Error('UNAVAILABLE'), { code: 'UNAVAILABLE' });
  const purchases = await service.listPurchases();
  const owned = purchases.find((p) => p.itemId === TV_PRODUCT_ID);
  if (!owned) throw Object.assign(new Error('NOT_FOUND'), { code: 'NOT_FOUND' });
  return verifyOnServer(owned.purchaseToken);
}

/** Licencja konta: { enforced, licensed, demoLeft } albo null (niezalogowany / błąd). */
export async function fetchAccountLicense() {
  const headers = authHeaders();
  if (!headers.Authorization) return null;
  try {
    const res = await fetch(`${API()}/account`, { headers });
    const data = await res.json().catch(() => ({}));
    return data?.ok ? data.license : null;
  } catch {
    return null;
  }
}
